import {
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Warning as WarningIcon
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { bankingService } from "../../services/bankingService";
import { t } from "../../translations";
import { createLogger } from "../../utils/logger";
import { openNotificationWithIcon } from "./notification-bar";

const logger = createLogger("StripeBankingDashboard");

const StripeBankingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);
  const [requirements, setRequirements] = useState(null);

  const user = useSelector((state) => state.user);
  const lang = useSelector((state) => state.lang);

  useEffect(() => {
    loadBankingStatus();
  }, []);

  const loadBankingStatus = async () => {
    try {
      const [statusResponse, requirementsResponse] = await Promise.all([
        bankingService.verifyAccount(user._id, user.refresh_token),
        bankingService.getRequirements(user._id, user.refresh_token)
      ]);

      setAccountStatus(statusResponse);
      setRequirements(requirementsResponse.requirements);
    } catch (error) {
      logger.error("Failed to load banking status:", error);

      // Handle "no account" case gracefully
      if (
        error.response?.status === 404 ||
        error.message?.includes("not found")
      ) {
        // No Stripe account exists yet - set pending status
        logger.info(
          "No Stripe Connect account found - user needs to start onboarding"
        );
        setAccountStatus({ status: "pending", payouts_enabled: false });
        setRequirements(null);
      } else {
        // Other errors - show error notification
        openNotificationWithIcon(
          "error",
          t("errors.failed_to_load_banking_status", lang)
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setLoading(true);
      const response = await bankingService.startOnboarding(
        user._id,
        user.refresh_token
      );
      window.location.href = response.accountLink;
    } catch (error) {
      logger.error("Failed to start onboarding:", error);
      openNotificationWithIcon(
        "error",
        t("errors.failed_to_start_onboarding", lang)
      );
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckIcon color="success" />;
      case "restricted":
        return <WarningIcon color="warning" />;
      case "pending":
        return <PendingIcon color="info" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "restricted":
        return "warning";
      case "pending":
        return "info";
      default:
        return "error";
    }
  };

  // Helper function to get translated requirement description
  const getRequirementDescription = (requirementKey) => {
    // Try to get translation using the nested key structure
    // e.g., "individual.dob.day" -> "common.stripe_connect.requirements.individual.dob.day"
    const translationKey = `common.stripe_connect.requirements.${requirementKey}`;
    const translation = t(translationKey, lang);

    // If translation returns the key itself, it means it wasn't found
    // Fall back to displaying the key in a more readable format
    if (translation === translationKey) {
      return requirementKey.split(".").pop().replace(/_/g, " ");
    }

    return translation;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("common.banking_setup", lang)}
      </Typography>

      {/* Banking Completion Status Banner */}
      {accountStatus?.status === "active" && accountStatus?.payouts_enabled && (
        <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            ✅ {t("common.stripe_connect.requirements.title", lang)} -{" "}
            {t("banking.complete", lang) || "Complete"}
          </Typography>
          <Typography variant="body2">
            {t("common.banking_details_verified_desc", lang)}
          </Typography>
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <BankIcon color="primary" />
            <Typography variant="h6">
              {t("common.stripe_connect.status_title", lang)}
            </Typography>
          </Box>

          <Alert
            severity={getStatusColor(accountStatus?.status)}
            icon={getStatusIcon(accountStatus?.status)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body1">
              {t(`banking.status.${accountStatus?.status || "pending"}`, lang)}
            </Typography>
            {accountStatus?.payouts_enabled && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                💰 Payouts enabled
              </Typography>
            )}
          </Alert>

          {accountStatus?.status !== "active" && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartOnboarding}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} /> : <BankIcon />
              }
            >
              {accountStatus?.status === "pending"
                ? t("banking.complete_setup", lang)
                : t("banking.start_setup", lang)}
            </Button>
          )}
        </CardContent>
      </Card>

      {requirements && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("banking.requirements", lang)}
            </Typography>

            {requirements.currently_due.length > 0 && (
              <>
                <Typography variant="subtitle1" color="error" gutterBottom>
                  {t("banking.currently_due", lang)}
                </Typography>
                <List>
                  {requirements.currently_due.map((req) => (
                    <ListItem key={req}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText primary={getRequirementDescription(req)} />
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {requirements.eventually_due.length > 0 && (
              <>
                <Typography
                  variant="subtitle1"
                  color="warning.main"
                  gutterBottom
                >
                  {t("banking.eventually_due", lang)}
                </Typography>
                <List>
                  {requirements.eventually_due.map((req) => (
                    <ListItem key={req}>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText primary={getRequirementDescription(req)} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {requirements.currently_due.length === 0 &&
              requirements.eventually_due.length === 0 && (
                <Alert severity="success">
                  {t("banking.no_requirements", lang)}
                </Alert>
              )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StripeBankingDashboard;
